import React, { useEffect, useMemo, useState } from "react";

import { Icon } from "../../assets/icons/Icon";
import { formatNumber, formatPrice } from "../../helpers/helpers";
import { SearchBar } from "../ui/search/SearchBar";
import styles from "./BillingTable.module.scss";
import { Discount, LineItem } from "./types";

interface BillingTableProps {
  lineItems: [string, LineItem[]][];
  discounts: Discount[];
}
const NO_AMOUNT = "$0.00";
// At least two decimals, but extend to the last non-zero digit so
// sub-cent unit prices ($0.015/page) don't round away to $0.00/$0.02.
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }).format(amount);
};

export const BillingTable: React.FC<BillingTableProps> = ({
  lineItems,
  discounts,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = (rowId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(rowId) ? next.delete(rowId) : next.add(rowId);

      return next;
    });
  };

  const filteredAndSortedInvoices = useMemo(() => {
    let result = [...lineItems];

    if (searchTerm) {
      result = result.filter((item) =>
        item[1].some((nestedItem) => {
          return nestedItem.name
            .toLowerCase()
            .includes(searchTerm.toLowerCase());
        }),
      );
    }

    return result;
  }, [lineItems, searchTerm]);

  // expand all items with nested_items by default
  useEffect(() => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      for (const [period, items] of filteredAndSortedInvoices) {
        for (const item of items) {
          if ((item.nested_items?.length ?? 0) > 0) {
            const rowId = `${period}::${item.name}`;
            if (!next.has(rowId)) next.add(rowId);
          }
        }
      }

      return next;
    });
  }, [filteredAndSortedInvoices]);

  return (
    <div className={styles.billingTable}>
      <div className={styles.billingTableHeader}>
        <h2 className={styles.billingTableTitle}>Cost by Feature</h2>
        <div className={styles.billingTableControls}>
          <div className={styles.billingTableSearch}>
            <SearchBar
              onChange={setSearchTerm}
              value={searchTerm}
              placeholder="Search feature by name"
            />
          </div>
        </div>
      </div>

      <div className={styles.billingTableTableWrapper}>
        <table className={styles.billingTableTable}>
          <thead>
            <tr>
              <th scope="col">Feature</th>
              <th scope="col">Interval</th>
              <th scope="col" className={styles.textRight}>
                QTY
              </th>
              <th scope="col" className={styles.textRight}>
                Unit Price
              </th>
              <th scope="col" className={styles.textRight}>
                Amount
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedInvoices.map(([period, items]) =>
              items.map((item, index) => {
                const isNoRowTitle = item.nested_items?.length === 0;
                const rowId = `${period}::${item.name}`;
                const isExpanded = expandedRows.has(rowId);

                return (
                  <React.Fragment key={`${period}-${item.name}-${index}`}>
                    <tr
                      // @ts-expect-error TS2551 non-existing className
                      className={styles.billingTable__row}
                      aria-expanded={isExpanded}
                    >
                      <td>
                        {item.nested_items?.length > 0 && (
                          <button
                            className={styles.billingTableExpandButton}
                            onClick={() => toggleRow(rowId)}
                            aria-label={`${isExpanded ? "Collapse" : "Expand"} ${item.name}`}
                          >
                            <span
                              // @ts-expect-error TS2551 non-existing className
                              className={`${styles.billingTableExpandIcon} ${isExpanded ? styles["billingTable__expandIcon--expanded"] : ""}`}
                            >
                              <Icon iconName="IconDown" />
                            </span>
                          </button>
                        )}
                        {item.name}
                      </td>
                      <td>{isNoRowTitle && period}</td>
                      <td className={styles.textRight}>
                        {isNoRowTitle && formatNumber(item.quantity)}
                      </td>
                      <td className={styles.textRight}>
                        {isNoRowTitle && item.unit_amount
                          ? formatCurrency(item.unit_amount)
                          : isNoRowTitle && NO_AMOUNT}
                        {isNoRowTitle &&
                          item.per_value &&
                          ` per ${item.per_value}`}
                      </td>
                      <td className={styles.textRight}>
                        {isNoRowTitle &&
                          (item.amount
                            ? formatCurrency(item.amount)
                            : NO_AMOUNT)}
                      </td>
                    </tr>
                    {item.nested_items?.length > 0 &&
                      isExpanded &&
                      item.nested_items.map((nestedItem, nestedIndex) => (
                        <tr
                          key={`${period}-${item.name}-nested-${nestedIndex}`}
                          className={styles.billingTableNestedRow}
                        >
                          <td className={styles.billingTableNestedCell}>
                            {nestedItem.name}
                          </td>
                          <td>{period}</td>
                          <td className={styles.textRight}>
                            {formatNumber(nestedItem.quantity)}
                          </td>
                          <td className={styles.textRight}>
                            {nestedItem.unit_amount
                              ? formatCurrency(nestedItem.unit_amount)
                              : NO_AMOUNT}
                          </td>
                          <td className={styles.textRight}>
                            {nestedItem.amount
                              ? formatCurrency(nestedItem.amount)
                              : NO_AMOUNT}
                          </td>
                        </tr>
                      ))}
                  </React.Fragment>
                );
              }),
            )}
            {discounts.map((discount, index) => {
              const name = discount.name || "Discount";
              let value;
              if (discount.amount_off && discount.currency) {
                value = formatPrice(discount.amount_off, {
                  currency: discount.currency,
                  input: "cents",
                });
              } else if (discount.percent_off) {
                value = `${discount.percent_off}%`;
              }

              return (
                <tr
                  // @ts-expect-error TS2551 non-existing className
                  className={styles.billingTable__row}
                  key={`discount-${index}`}
                >
                  <td>{name}</td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td className={styles.textRight}>{value}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
